import { createFileRoute } from "@tanstack/react-router";
import Link from "@/components/link";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { MetricToggleChart } from "@/components/metric-chart";
import { DataTable } from "@/components/data-table";
import { ProductThumb } from "@/components/product-thumb";
import { ChartCard, EmptyChart } from "@/components/charts/chart-card";
import { RankList } from "@/components/charts/rank-list";
import { AttributionCard } from "@/components/charts/attribution-card";
import { FunnelCard } from "@/components/charts/funnel-card";
import { ParamChips } from "@/components/analytics/param-chips";
import { CompareNote, DataNote } from "@/components/analytics/report-shell";
import { StoreSampleBanner } from "@/components/analytics/store-sample-banner";
import { JourneyButton } from "@/components/journey-dialog";
import { Badge } from "@/components/ui/badge";
import { SkStorePage } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { DEFAULT_PRESET } from "@shared/analytics-range";
import { defaultGrain, grainOptions, withRange } from "@shared/store-report";
import { fmtDateTime, fmtNum, fmtPct } from "@shared/format";
import type { ProductJourneys } from "@shared/shoppers-types";
import type { ProductPage } from "@shared/pages/product";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/_app/products/$id")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, params, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<ProductPage>(`product/${params.id}`, deps));
  },
  component: ProductRoute,
});

function ProductRoute() {
  useTitle("Product");
  const { id } = Route.useParams();
  const search = Route.useSearch();
  return (
    <PageQuery<ProductPage> name={`product/${id}`} search={search} skeleton={<SkStorePage backLink row="pair" />} notFound="product">
      {(page) => <Product page={page} />}
    </PageQuery>
  );
}

function Product({ page }: { page: ProductPage }) {
  useTitle(page.title);
  const { range: r, status } = page;
  const ads = page.ads;

  return (
    <div className="page-shell">
      <Link href={withRange("/products", r.params)} className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-accent-dark hover:underline">
        <span aria-hidden>←</span> All products
      </Link>
      <PageHeader
        leading={<ProductThumb src={page.imageUrl} size={64} />}
        title={page.title}
        actions={<DateRangeFilter presets={r.presets} defaultKey={DEFAULT_PRESET} compare />}
      />
      <StoreSampleBanner
        sales={page.sources.sales === "sample"}
        ads={page.sources.ads === "sample"}
        ga={page.sources.ga === "sample"}
        salesWhat="Product"
      />
      <DataNote status={status} staleAfterDays={3} />

      <MetricToggleChart
        labels={page.labels}
        metrics={page.metrics}
        height="clamp(200px, 17cqw, 280px)"
        hint={`From Shopify.${page.hasPrevious ? " Dashed lines are the previous period." : ""}`}
        right={<ParamChips param="by" options={grainOptions()} defaultValue={defaultGrain(r)} />}
      />
      <CompareNote range={r} />

      <div className="mt-4 grid gap-4 @3xl:grid-cols-2">
        <AttributionCard title="Where its orders came from" legend="right" views={page.attribution} />

        <ChartCard title="Google Ads for this product" hint="From Google Ads. Google credits each sale to the product whose ad was clicked.">
          {ads.state === "missing" ? (
            <EmptyChart text={ads.text} />
          ) : ads.state === "ok" ? (
            <div className="space-y-4">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 @sm:grid-cols-3">
                <Stat label="Spend" value={ads.stats.spend} />
                <Stat label="Clicks" value={ads.stats.clicks} />
                <Stat label="Times shown" value={ads.stats.impressions} />
                <Stat label="Orders (Google)" value={ads.stats.orders} />
                <Stat label="Google-reported sales" value={ads.stats.sales} />
                <Stat label="Back per $1" value={ads.stats.back} />
              </dl>
              {ads.types.length > 0 && (
                <div className="border-t border-border pt-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted">Spend by campaign type</p>
                  <RankList compact format="usd" items={ads.types} />
                </div>
              )}
            </div>
          ) : (
            <EmptyChart text={ads.text} />
          )}
        </ChartCard>

        <FunnelCard
          title="From view to purchase"
          hint="From Google Analytics."
          unit="shoppers"
          steps={page.funnel.steps}
          empty={<EmptyChart text={page.funnel.steps ? undefined : page.funnel.empty} />}
        />

        <ChartCard title="By variant" hint="From Shopify.">
          <DataTable
            columns={[
              { key: "title", label: "Variant" },
              { key: "units", label: "Sold", format: "num", align: "right", total: "sum" },
              { key: "netSales", label: "Shopify sales", format: "usd", align: "right", total: "sum" },
            ]}
            rows={page.variants}
            rowKey="key"
            defaultSort="netSales"
            pageSize={10}
            emptyText="No variants sold in this range."
          />
        </ChartCard>

        {page.shoppers && <ShoppersCard data={page.shoppers} />}
      </div>
    </div>
  );
}

const OUTCOME: Record<ProductJourneys["recent"][number]["outcome"], { text: string; tone: "green" | "blue" | "slate" }> = {
  bought: { text: "Bought it", tone: "green" },
  other: { text: "Bought something else", tone: "blue" },
  left: { text: "Left", tone: "slate" },
};

function ShoppersCard({ data }: { data: ProductJourneys }) {
  const share = (n: number) => (data.viewers ? fmtPct(n / data.viewers) : "–");
  return (
    <ChartCard
      className="@3xl:col-span-2"
      title="Shoppers who looked at this"
      hint="Shoppers (browsers) who viewed this product in the range and what they did after. From the shopper pixel."
    >
      {data.viewers ? (
        <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 @3xl:grid-cols-4">
            <Stat label="Viewed it" value={fmtNum(data.viewers)} />
            <Stat label="Bought it" value={`${fmtNum(data.boughtIt)} (${share(data.boughtIt)})`} />
            <Stat label="Bought something else" value={fmtNum(data.boughtOther)} />
            <Stat label="Left without buying" value={fmtNum(data.left)} />
          </dl>
          <ul className="divide-y divide-border border-t border-border">
            {data.recent.map((v) => (
              <li key={v.browserId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
                <span className="w-40 shrink-0 tabular-nums text-muted">{fmtDateTime(v.lastView)}</span>
                <span className="min-w-0 flex-1 truncate text-foreground">
                  {[v.location, v.device ? v.device.charAt(0).toUpperCase() + v.device.slice(1) : null].filter(Boolean).join(" · ") || "Unknown place"}
                </span>
                <Badge tone={OUTCOME[v.outcome].tone}>
                  <span className="normal-case">{OUTCOME[v.outcome].text}</span>
                </Badge>
                <JourneyButton target={{ kind: "visitor", id: v.browserId }} title="Open the shopper journey">
                  Journey
                </JourneyButton>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <EmptyChart text="No shopper viewed this product in this range." />
      )}
    </ChartCard>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-[11px] font-semibold uppercase tracking-wider text-muted">{label}</dt>
      <dd className="mt-0.5 font-heading text-lg font-bold tabular-nums text-foreground">{value}</dd>
    </div>
  );
}
